import * as React from "react";
import { graphMailService, type VorgangEntwurf } from "../../../services/graphMailService";
import { abonniereEntwurfAenderung } from "../../mail/entwurfBus";
import { openEntwurfWindow } from "../../mail/EntwurfFenster";
import { useWindowManagerOptional } from "../../windowManager/WindowManagerContext";
import { IcoMail, IcoPapierkorb } from "../v3/vg3Icons";
import { bestaetigen, hinweisen } from "../../redesign/bestaetigen";
import { istNichtMehrVorhanden } from "../../mail/mailRowActions";

/**
 * T-231 (Admin 12.10.2026: „im Vorgang, Entwurf ablegen") — Mail-Entwürfe, die
 * zu diesem Vorgang gehören, stehen im Vorgang.
 *
 * Seit dem 22.10.2026 (User: „Mail-Entwurf — sowie Mail im Verlauf mit
 * Kommentar anzeigen") stehen sie als KARTE IM VERLAUF ihres Strangs — wie
 * eine Mail, mit Kommentarspalte, gekennzeichnet als „Entwurf"
 * (`entwurfZeilen` in `archivZeilen.ts`, Darstellung in `EreignisListeV4`).
 * Klick schreibt weiter, der Papierkorb löscht (landet in „Gelöschte
 * Elemente"). Nur für Entwürfe am Vorgang, wenn er To-dos, aber keinen eigenen
 * Strang hat, bleibt die kleine Karte {@link VorgangEntwuerfeV4}.
 *
 * „Gehört zum Vorgang" entscheidet der Server (`entwurfImVorgang`): aus dem
 * Vorgang/To-do heraus begonnen ODER Antwort/Weiterleitung auf eine Mail des
 * Vorgangs. `todoId` sagt, an welchem To-do der Entwurf steht.
 *
 * Die Entwürfe liegen weiter im Postfach (Graph) — der Vorgang zeigt sie nur.
 * Speichern, Senden und Löschen melden sich über den Entwurfs-Bus; dann lädt die
 * Liste neu. Nach dem Senden aus dem Entwurfsfenster lädt der Vorgang seinen
 * Verlauf neu (`onGesendet`) — aus dem Entwurf wird die gesendete Mail, und
 * seine Kommentare hängen dann an ihr (Server: `uebernehmeEntwurfsKommentare`).
 */
export function entwurfZeit(iso: string | null, heute: Date = new Date()): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const zwei = (n: number) => String(n).padStart(2, "0");
    const uhr = `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
    const gleicherTag =
        d.getFullYear() === heute.getFullYear() && d.getMonth() === heute.getMonth() && d.getDate() === heute.getDate();
    if (gleicherTag) return `heute ${uhr}`;
    const datum = `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.`;
    return d.getFullYear() === heute.getFullYear() ? `${datum} ${uhr}` : `${datum}${d.getFullYear()} ${uhr}`;
}

/** Lädt die Entwürfe eines Vorgangs und hält sie über den Entwurfs-Bus aktuell. */
export function useVorgangEntwuerfe(
    vorgangId: number | null | undefined,
    /** Testnaht. */
    laden?: (vorgangId: number) => Promise<VorgangEntwurf[]>,
): VorgangEntwurf[] | null {
    // `null` = noch nicht geladen. Bis dahin bleiben Kommentare an Entwürfen
    // hinter ihrer (noch fehlenden) Karte, statt kurz als Block aufzublitzen.
    const [entwuerfe, setEntwuerfe] = React.useState<VorgangEntwurf[] | null>(null);
    const [tick, setTick] = React.useState(0);

    React.useEffect(() => abonniereEntwurfAenderung(() => setTick((t) => t + 1)), []);

    // In Outlook gesendet oder gelöscht, meldet sich kein Bus (Befund
    // 03.11.2026: die Karte „Entwurf“ blieb nach dem Senden aus Outlook
    // stehen). Wer zurück ins Fenster kommt, sieht deshalb den frischen Stand.
    React.useEffect(() => {
        const neu = () => {
            if (document.visibilityState === "visible") setTick((t) => t + 1);
        };
        window.addEventListener("focus", neu);
        document.addEventListener("visibilitychange", neu);
        return () => {
            window.removeEventListener("focus", neu);
            document.removeEventListener("visibilitychange", neu);
        };
    }, []);

    React.useEffect(() => {
        if (vorgangId == null) return;
        let verworfen = false;
        const holen = laden ?? graphMailService.fetchVorgangEntwuerfe;
        // Nachrangig: fehlt der Abruf oder scheitert er, steht einfach nichts da.
        if (typeof holen !== "function") return;
        void Promise.resolve(holen(vorgangId))
            .then((liste) => {
                if (!verworfen) setEntwuerfe(Array.isArray(liste) ? liste : []);
            })
            .catch(() => {
                if (!verworfen) setEntwuerfe([]);
            });
        return () => {
            verworfen = true;
        };
    }, [vorgangId, tick, laden]);

    return entwuerfe;
}

/**
 * Verteilt die Entwürfe auf ihre Orte: je To-do (nur To-dos, die es auf der
 * Seite gibt) und der Rest an den Vorgang — ein Entwurf geht nie verloren.
 */
export function verteileEntwuerfe(
    entwuerfe: ReadonlyArray<VorgangEntwurf> | null,
    todoIds: ReadonlyArray<number>,
): { amVorgang: VorgangEntwurf[]; jeTodo: Map<number, VorgangEntwurf[]> } {
    const bekannt = new Set(todoIds);
    const amVorgang: VorgangEntwurf[] = [];
    const jeTodo = new Map<number, VorgangEntwurf[]>();
    for (const e of entwuerfe ?? []) {
        const t = e.todoId ?? null;
        if (t != null && bekannt.has(t)) {
            const liste = jeTodo.get(t) ?? [];
            liste.push(e);
            jeTodo.set(t, liste);
        } else {
            amVorgang.push(e);
        }
    }
    return { amVorgang, jeTodo };
}

/**
 * Weiterschreiben und Löschen eines Entwurfs — für die Verlaufskarte und die
 * kleine Karte. Löschen fragt im eigenen Popup nach; ein schon verschwundener
 * Entwurf (gesendet/gelöscht, auch in Outlook) ist kein Fehler.
 */
export function useEntwurfAktionen(onGesendet?: () => void | Promise<void>): {
    oeffnen: (e: VorgangEntwurf) => void;
    loeschen: (e: VorgangEntwurf) => Promise<void>;
    loeschend: string | null;
} {
    // Optional: der Strang steht auch ohne Fensterverwaltung (Tests,
    // eingebettete Ansichten) — dann öffnet der Klick eben nichts.
    const openWindow = useWindowManagerOptional()?.openWindow;
    const [loeschend, setLoeschend] = React.useState<string | null>(null);
    const oeffnen = React.useCallback(
        (e: VorgangEntwurf) => {
            if (!openWindow) return;
            openEntwurfWindow(openWindow, {
                mailbox: e.mailbox,
                draftId: e.draftId,
                titel: e.subject,
                ...(onGesendet ? { onSent: onGesendet } : {}),
            });
        },
        [openWindow, onGesendet],
    );
    const loeschen = React.useCallback(async (e: VorgangEntwurf) => {
        const ok = await bestaetigen({
            titel: "Entwurf löschen?",
            text: `Der Entwurf „${e.subject || "(kein Betreff)"}“ wird in „Gelöschte Elemente“ verschoben.`,
            ja: "Löschen",
        });
        if (!ok) return;
        setLoeschend(e.draftId);
        try {
            await graphMailService.deleteDraft(e.mailbox, e.draftId);
        } catch (err) {
            // Schon weg — die Liste lädt über den Bus ohnehin neu.
            if (!istNichtMehrVorhanden(err)) {
                void hinweisen({
                    titel: "Entwurf nicht gelöscht",
                    text: err instanceof Error ? err.message : "Der Entwurf konnte nicht gelöscht werden.",
                    ton: "danger",
                });
            }
        } finally {
            setLoeschend(null);
        }
    }, []);
    return { oeffnen, loeschen, loeschend };
}

/**
 * Die Entwurfszeilen — „Entwurf" als Marke, Betreff, Empfänger, Stand, Postfach.
 * Ohne Entwürfe erscheint nichts.
 */
export const EntwurfZeilenV4: React.FC<{
    entwuerfe: ReadonlyArray<VorgangEntwurf>;
    /** Nach dem Senden aus dem Entwurfsfenster (Verlauf neu laden). */
    onGesendet?: () => void | Promise<void>;
    /** Mit Überschrift „Mail-Entwurf"/„Mail-Entwürfe (n)". */
    mitTitel?: boolean;
}> = ({ entwuerfe, onGesendet, mitTitel = true }) => {
    const { oeffnen, loeschen, loeschend } = useEntwurfAktionen(onGesendet);

    if (entwuerfe.length === 0) return null;

    return (
        <section className="vg4-entwuerfe" aria-label="Mail-Entwürfe">
            {mitTitel && (
                <div className="vg3-abschnitt-h">
                    {entwuerfe.length === 1 ? "Mail-Entwurf" : `Mail-Entwürfe (${entwuerfe.length})`}
                </div>
            )}
            <ul className="vg4-entwuerfe-liste">
                {entwuerfe.map((e) => (
                    <li key={e.draftId} className="vg4-entwurf-zeile">
                        <button
                            type="button"
                            className="vg4-entwurf-oeffnen"
                            title="Entwurf weiterschreiben"
                            onClick={() => oeffnen(e)}
                        >
                            <IcoMail size={15} />
                            <span className="vg4-entwurf-marke">Entwurf</span>
                            <span className="vg4-entwurf-betreff">{e.subject || "(kein Betreff)"}</span>
                            <span className="vg4-entwurf-meta">
                                {[e.to.length ? `an ${e.to.join(", ")}` : null, entwurfZeit(e.lastModifiedDateTime), e.mailbox]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </span>
                        </button>
                        <button
                            type="button"
                            className="vg3-ibtn"
                            title="Entwurf löschen"
                            aria-label={`Entwurf „${e.subject || "(kein Betreff)"}“ löschen`}
                            disabled={loeschend === e.draftId}
                            onClick={() => void loeschen(e)}
                        >
                            <IcoPapierkorb size={15} />
                        </button>
                    </li>
                ))}
            </ul>
        </section>
    );
};

/**
 * Eigene Karte für die Entwürfe am Vorgang, wenn er To-dos hat (dann trägt er
 * keinen eigenen Strang, in dem sie stehen könnten).
 */
export const VorgangEntwuerfeV4: React.FC<{
    entwuerfe: ReadonlyArray<VorgangEntwurf>;
    onGesendet?: () => void | Promise<void>;
}> = ({ entwuerfe, onGesendet }) => {
    if (entwuerfe.length === 0) return null;
    return (
        <div className="vg4-card">
            <EntwurfZeilenV4 entwuerfe={entwuerfe} {...(onGesendet ? { onGesendet } : {})} />
        </div>
    );
};
